import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CampaignPromoPerformance } from './types';

interface UseCampaignPromoPerformanceReturn {
  performance: CampaignPromoPerformance | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useCampaignPromoPerformance = (
  campaignId: number | null
): UseCampaignPromoPerformanceReturn => {
  const [performance, setPerformance] = useState<CampaignPromoPerformance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformance = async () => {
    if (!campaignId) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('calculate_campaign_promo_performance', {
        p_campaign_id: campaignId
      });

      if (rpcError) {
        throw rpcError;
      }

      if (data && data.length > 0) {
        const rawPerformance = data[0];
        
        // Transform the raw data and calculate additional metrics
        const transformedPerformance: CampaignPromoPerformance = {
          campaign_id: rawPerformance.campaign_id,
          promo_code: rawPerformance.promo_code,
          promo_code_id: rawPerformance.promo_code_id,
          orders_count: rawPerformance.orders_count,
          gross_revenue: rawPerformance.gross_revenue,
          total_discount_value: rawPerformance.total_discount_value,
          sms_cost: rawPerformance.sms_cost,
          net_revenue: rawPerformance.net_revenue,
          net_profit: rawPerformance.net_profit,
          attribution_start_date: rawPerformance.attribution_start_date,
          attribution_end_date: rawPerformance.attribution_end_date
        };

        setPerformance(transformedPerformance);
      } else {
        setPerformance(null);
      }
    } catch (err) {
      console.error('Error fetching campaign promo performance:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch performance data');
      setPerformance(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = async () => {
    await fetchPerformance();
  };

  useEffect(() => {
    if (campaignId) {
      fetchPerformance();
    } else {
      setPerformance(null);
      setError(null);
    }
  }, [campaignId]);

  return {
    performance,
    isLoading,
    error,
    refetch
  };
};

// Helper function to calculate additional metrics that aren't in the database function
export const calculateAdditionalMetrics = (
  performance: CampaignPromoPerformance,
  totalRecipients: number,
  itemMenuPrice?: number
) => {
  const conversionRate = totalRecipients > 0 ? (performance.orders_count / totalRecipients) * 100 : 0;
  const averageOrderValue = performance.orders_count > 0 ? performance.gross_revenue / performance.orders_count : 0;
  const valueProvided = itemMenuPrice ? itemMenuPrice * performance.orders_count : 0;

  return {
    conversionRate,
    averageOrderValue,
    valueProvided,
    costPerOrder: performance.orders_count > 0 ? performance.sms_cost / performance.orders_count : 0,
    returnOnInvestment: performance.sms_cost > 0 ? (performance.net_profit / performance.sms_cost) * 100 : 0
  };
};